import React, { useEffect, useMemo, useState } from 'react';
import { TrashCanOutlined } from '@duxton-web/icons';
import {
  Button,
  ConfigProvider,
  Flex,
  Form,
  Select,
  Typography,
} from '@duxton-web/ui';
import styled from 'styled-components';

import {
  ExpFormField,
  PopulationTargetMode,
  PopulationTargetType,
} from '../../helper/consts';
import { AndTag } from './MetricRow';
import {
  Dimension,
  DimensionFilterConstraint,
  DraftDimensionFilterConstraint,
} from './types';
import { combineDimensionValues } from './utils';
import GenericFuseSelect from '@/common/selects/GenericFuseSelect/GenericFuseSelect';
import Service from '@/lib/App';
import { Operators } from '@/lib/Configurations';
import {
  transformPopulationTargetValueToId,
  type TargetValues,
} from '@/utils/experiment';
import { error } from '@/utils/logging';

const LARGE_LIST_THRESHOLD = 100;

// dimension values are stored as string in the backend,
// but dimensionValues value can be string or number
// so we need to normalize dimension values so that the select component can display the correctly
const normalizedValues = (values: any, dimensionValues) => {
  if (!Array.isArray(values)) {
    return values;
  }

  if (dimensionValues.length && typeof dimensionValues[0].value === 'number') {
    return values.map((value) => +value);
  }

  return values;
};

export const MetricFilter = ({
  metricName,
  constraint,
  dimensions,
  onDelete,
  onUpdate,
  showValidation,
  isViewMode,
}: {
  metricName: string;
  constraint: DimensionFilterConstraint | DraftDimensionFilterConstraint;
  dimensions: Dimension[];
  onDelete: () => void;
  onUpdate: (constraint: DraftDimensionFilterConstraint) => void;
  showValidation: boolean;
  isViewMode: boolean;
}) => {
  const [dimensionValues, setDimensionValues] = useState<
    { value: string | boolean | number; display_value: string }[]
  >([]);
  const [fetchingDimensionValues, setFetchingDimensionValues] = useState(false);
  // Store full list of dimension values (without filters) to preserve labels for selected values
  const [fullDimensionValues, setFullDimensionValues] = useState<
    { value: string | boolean | number; display_value: string }[]
  >([]);

  const form = Form.useFormInstance();
  const populationTargetMode = Form.useWatch(ExpFormField.TARGET.MODE, {
    form,
    preserve: true,
  });
  const populationTargetType = Form.useWatch(ExpFormField.TARGET.TYPE, {
    form,
    preserve: true,
  });
  const populationTargetValue: TargetValues | null = Form.useWatch(
    ExpFormField.TARGET.VALUE,
    {
      form,
      preserve: true,
    },
  );

  // Omit `dimensions` from the dependency array because MetricFilter will be unmounted when the `dimensions` change
  const dimension = useMemo(() => {
    const dimension = dimensions.find(
      (d) => d.name === constraint.dimension_name,
    );
    if (!dimension) {
      error(
        `Unexpected branch - Dimension ${constraint.dimension_name} not found`,
      );
      return null;
    }

    return dimension;
  }, [constraint.dimension_name]);

  // Fetch full dimension values (without filters) when dimension changes
  useEffect(() => {
    if (!dimension) {
      setFullDimensionValues([]);
      return;
    }

    if (dimension.data_type === 'BOOLEAN') {
      const booleanValues = [
        {
          value: true,
          display_value: 'True',
        },
        {
          value: false,
          display_value: 'False',
        },
      ];
      setFullDimensionValues(booleanValues);
      return;
    }

    // Fetch full list without any filters
    Service.planner
      .fetchDimensionValues(metricName, constraint.dimension_name, {
        cityIds: null,
        countryIds: null,
      })
      .then((values) => {
        setFullDimensionValues(values.dimension_values);
      });
  }, [dimension, metricName]);

  // Fetch filtered dimension values based on population filters
  useEffect(() => {
    if (!dimension) {
      return;
    }

    if (!populationTargetMode) {
      // do not fetch dimension values if form data is not mounted to avoid duplicated request and race condition
      return;
    }

    if (dimension.data_type === 'BOOLEAN') {
      const booleanValues = [
        {
          value: true,
          display_value: 'True',
        },
        {
          value: false,
          display_value: 'False',
        },
      ];
      setDimensionValues(booleanValues);

      return;
    }

    let cityIds: string | null = null;
    let countryIds: string | null = null;

    const transformedPopulationTargetValue = transformPopulationTargetValueToId(
      populationTargetValue,
    );

    if (
      populationTargetMode === PopulationTargetMode.DYNAMIC &&
      populationTargetType === PopulationTargetType.CITY &&
      !!populationTargetValue
    ) {
      cityIds = transformedPopulationTargetValue;
    }

    if (
      populationTargetMode === PopulationTargetMode.DYNAMIC &&
      populationTargetType === PopulationTargetType.COUNTRY &&
      !!populationTargetValue
    ) {
      countryIds = transformedPopulationTargetValue;
    }

    setFetchingDimensionValues(true);

    Service.planner
      .fetchDimensionValues(metricName, constraint.dimension_name, {
        cityIds,
        countryIds,
      })
      .then((values) => {
        setDimensionValues(values.dimension_values);
      })
      .finally(() => {
        setFetchingDimensionValues(false);
      });
  }, [
    isViewMode,
    constraint.dimension_name,
    populationTargetMode,
    populationTargetType,
    populationTargetValue,
    metricName,
  ]);

  // Combine filtered dimension values with full list to ensure selected values have labels
  const combinedDimensionValues = useMemo(
    () =>
      combineDimensionValues(
        dimensionValues,
        fullDimensionValues,
        constraint.values,
      ),
    [dimensionValues, fullDimensionValues, constraint.values],
  );

  const shouldUseFuseSelect = dimensionValues.length > LARGE_LIST_THRESHOLD;

  const handleFieldChange = (
    updatedConstraint: Partial<DraftDimensionFilterConstraint>,
  ) => {
    onUpdate({
      ...constraint,
      ...updatedConstraint,
    });
  };

  const isValueEmpty = useMemo(() => {
    if (!constraint.values) return true;
    if (Array.isArray(constraint.values)) return constraint.values.length === 0;
    return false;
  }, [constraint.values]);

  const valueDisabled =
    isViewMode || fetchingDimensionValues || !constraint.operator;

  return (
    <>
      <Flex align="stretch" gap={8}>
        <AndTag>AND</AndTag>
        <Flex wrap="wrap" gap={8}>
          <Select
            style={{ minWidth: 200 }}
            showSearch
            onChange={(value) =>
              handleFieldChange({
                dimension_name: value,
              })
            }
            placeholder="Select filter"
            value={constraint.dimension_name}
            options={dimensions.map((dimension) => ({
              key: dimension.name,
              value: dimension.name,
              label: dimension.display_name,
            }))}
            disabled={isViewMode}
          />
          <Select
            showSearch
            onChange={(value) =>
              handleFieldChange({
                operator: value,
                values: null,
              })
            }
            placeholder="Select operator"
            value={constraint.operator}
            options={[Operators['in'], Operators['not in']].map((operator) => ({
              key: operator,
              value: operator,
              label: operator,
            }))}
            style={{ width: 'fit-content' }}
            popupMatchSelectWidth={false}
            status={
              showValidation && !constraint.operator ? 'error' : undefined
            }
            disabled={isViewMode}
          />
          <div style={{ flexGrow: 1 }}>
            {shouldUseFuseSelect && !isViewMode ? (
              <GenericFuseSelect
                data-testid="dimension-values-select"
                items={combinedDimensionValues}
                value={normalizedValues(
                  constraint.values,
                  combinedDimensionValues,
                )}
                onChange={(newValue) => {
                  handleFieldChange({
                    values: newValue || null,
                  });
                }}
                mode={
                  [Operators.in, Operators['not in']].includes(
                    constraint.operator,
                  )
                    ? 'multiple'
                    : null
                }
                disabled={fetchingDimensionValues || !constraint.operator}
                keyFields={{
                  valueKey: 'value',
                  displayKey: 'display_value',
                }}
                status={showValidation && isValueEmpty ? 'error' : undefined}
                style={{ minWidth: 300 }}
              />
            ) : (
              <Select
                data-testid="dimension-values-select"
                mode={
                  [Operators.in, Operators['not in']].includes(
                    constraint.operator,
                  )
                    ? 'multiple'
                    : undefined
                }
                style={{ minWidth: 300 }}
                showSearch
                onChange={(value) =>
                  handleFieldChange({
                    values: value || null,
                  })
                }
                placeholder="Select value"
                value={normalizedValues(
                  constraint.values,
                  combinedDimensionValues,
                )}
                disabled={valueDisabled}
                loading={fetchingDimensionValues}
                options={combinedDimensionValues.map((item) => ({
                  key: item.value,
                  value: item.value,
                  label: item.display_value,
                }))}
                status={showValidation && isValueEmpty ? 'error' : undefined}
              />
            )}
          </div>
        </Flex>
        {!isViewMode && (
          <ConfigProvider
            theme={{
              token: {
                fontWeightStrong: 400,
              },
              components: {
                Button: {
                  onlyIconSize: 14,
                  defaultBorderColor: '#bfbfbf',
                },
              },
            }}
          >
            <Button
              data-testid="filter-delete"
              shape="default"
              style={{
                flex: '0 0 40px',
                alignSelf: 'flex-start',
              }}
              icon={<TrashCanOutlined />}
              onClick={onDelete}
            />
          </ConfigProvider>
        )}
      </Flex>
      {showValidation &&
        (!constraint.dimension_name ||
          !constraint.operator ||
          isValueEmpty) && (
          <ValidationText type="danger" style={{ marginLeft: 48 }}>
            Please complete all filter fields
          </ValidationText>
        )}
    </>
  );
};

const ValidationText = styled(Typography.Text)`
  font-size: 12px;
`;
